import React from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface WorkSummaryCardProps {
  contracts: number;
  supplies: number;
  products: number;
  quotationsSent: number;
  quotationsAccepted: number;
  quotationsClosed: number;
}

const WorkSummaryCard: React.FC<WorkSummaryCardProps> = ({
  contracts,
  supplies,
  products,
  quotationsSent,
  quotationsAccepted,
  quotationsClosed,
}) => {
  const { theme, isDark } = useTheme();
  const navigation = useNavigation<any>();

  const handlePress = () => {
    navigation.navigate('Work');
  };

  return (
    <TouchableOpacity 
      style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: theme.warning + '20' }]}>
          <Ionicons name="briefcase-outline" size={24} color={theme.warning} />
        </View>
        <ThemedText style={[styles.title, { color: theme.text }]}>
          Work Summary
        </ThemedText>
      </View>

      <View style={styles.statsContainer}>
        <View style={styles.statItem}>
          <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
            My Contracts
          </ThemedText>
          <ThemedText style={[styles.statValue, { color: theme.text }]}>
            {contracts}
          </ThemedText>
        </View>

        <View style={[styles.divider, { backgroundColor: theme.border }]} />

        <View style={styles.statItem}>
          <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
            My Supplies
          </ThemedText>
          <ThemedText style={[styles.statValue, { color: theme.text }]}>
            {supplies}
          </ThemedText>
        </View>

        <View style={[styles.divider, { backgroundColor: theme.border }]} />

        <View style={styles.statItem}>
          <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
            My Products
          </ThemedText>
          <ThemedText style={[styles.statValue, { color: theme.text }]}>
            {products}
          </ThemedText>
        </View>
      </View>

      <View style={styles.quotationsTitleContainer}>
        <ThemedText style={[styles.quotationsTitle, { color: theme.text }]}>
          Quotations
        </ThemedText>
      </View>

      <View style={styles.quotationsContainer}>
        <View style={styles.quotationItem}>
          <ThemedText style={[styles.quotationLabel, { color: theme.textSecondary }]}>
            Sent
          </ThemedText>
          <ThemedText style={[styles.quotationValue, { color: theme.warning }]}>
            {quotationsSent || 0}
          </ThemedText>
        </View>

        <View style={styles.quotationItem}>
          <ThemedText style={[styles.quotationLabel, { color: theme.textSecondary }]}>
            Accepted
          </ThemedText>
          <ThemedText style={[styles.quotationValue, { color: theme.info }]}>
            {quotationsAccepted || 0}
          </ThemedText>
        </View>

        <View style={styles.quotationItem}>
          <ThemedText style={[styles.quotationLabel, { color: theme.textSecondary }]}>
            Completed
          </ThemedText>
          <ThemedText style={[styles.quotationValue, { color: theme.success }]}>
            {quotationsClosed || 0}
          </ThemedText>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    padding: 18,
    marginBottom: 16,
    borderWidth: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 11,
    marginBottom: 6,
    textAlign: 'center',
  },
  statValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  divider: {
    width: 1,
    height: 40,
    marginHorizontal: 8,
  },
  quotationsTitleContainer: {
    paddingTop: 16,
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.08)',
  },
  quotationsTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  quotationsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 8,
  },
  quotationItem: {
    alignItems: 'center',
    flex: 1,
  },
  quotationLabel: {
    fontSize: 11,
    marginBottom: 6,
    textAlign: 'center',
  },
  quotationValue: {
    fontSize: 18,
    fontWeight: '700',
  },
});

export default WorkSummaryCard;
